/**
 * QueueTable Component
 *
 * Support queue table with filtering, sorting, and quick actions.
 * Displays support requests with status, priority, and assignment info.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-149 - Support Dashboard (26 SP)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  User as _User,
  AlertCircle as _AlertCircle,
  CheckCircle,
  Circle,
  ArrowRight,
  User
} from 'lucide-react';

export function QueueTable({ queue, loading, onAssign: _onAssign, onUpdateStatus: _onUpdateStatus }) {
  const navigate = useNavigate();

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'text-red-700 bg-red-100',
      high: 'text-orange-700 bg-orange-100',
      medium: 'text-yellow-700 bg-yellow-100',
      low: 'text-gray-700 bg-gray-100',
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: priority is enum from database
    return colors[priority] || colors.low;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Circle className="w-4 h-4 text-yellow-600" />,
      in_progress: <Clock className="w-4 h-4 text-blue-600" />,
      resolved: <CheckCircle className="w-4 h-4 text-green-600" />,
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: status is enum from database
    return icons[status] || icons.pending;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-700 bg-yellow-100',
      in_progress: 'text-blue-700 bg-blue-100',
      resolved: 'text-green-700 bg-green-100',
    };
    // eslint-disable-next-line security/detect-object-injection -- safe: status is enum from database
    return colors[status] || colors.pending;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {return 'Just now';}
    if (diffMins < 60) {return `${diffMins}m ago`;}
    if (diffHours < 24) {return `${diffHours}h ago`;}
    if (diffDays < 7) {return `${diffDays}d ago`;}
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!queue || queue.items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <_AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No support requests</h3>
        <p className="text-sm text-gray-500">There are no support requests in the queue</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Request
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {queue.items.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/support/queue/${item.id}`)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(item.status)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.subject}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.request_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{item.user_name}</div>
                  <div className="text-xs text-gray-500">{item.user_email}</div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                      item.priority
                    )}`}
                  >
                    {item.priority}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                      item.status
                    )}`}
                  >
                    {item.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {item.assigned_to_name ? (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {item.assigned_to_name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Unassigned</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-500">
                    {formatDate(item.created_at)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={(  _e) => {
                      _e.stopPropagation();
                      navigate(`/support/queue/${item.id}`);
                    }}
                    className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-sm font-medium"
                  >
                    View
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {queue.pagination && queue.pagination.total > queue.pagination.limit && (
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing{' '}
            <span className="font-medium">{queue.pagination.offset + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(
                queue.pagination.offset + queue.pagination.limit,
                queue.pagination.total
              )}
            </span>{' '}
            of <span className="font-medium">{queue.pagination.total}</span> results
          </div>
          <div className="flex gap-2">
            <button
              disabled={queue.pagination.offset === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              disabled={!queue.pagination.has_more}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueueTable;
